import { useMemo, useState } from 'react';
import { Plus as PlusIcon, Trash2 as TrashIcon, ChevronDown, ChevronRight } from 'lucide-react';

export interface PlanForm {
  codigo: string;
  nombre: string;
  sucursalId: string;
  categoriaAfiliadoCodigo: string;
  aaCodigo: string;
  aa2Codigo: string;
  esBase: boolean;
  /** Disponible para la venta: solo estos se le ofrecen al promotor en un padrón. */
  disponibleVenta?: boolean;
}

/**
 * Lista de planes de una organización.
 *
 * Antes cada plan era una tarjeta con seis campos siempre visibles: con MS
 * (2 planes) se leía bien, pero traer la config de una empresa grande devuelve
 * 89 planes y la pantalla se volvía imposible de recorrer. Acá cada plan es
 * UNA fila —código, nombre y si es el base— y los códigos de Binary se abren
 * solo en el que estás editando. Con buscador, porque a partir de cierto
 * número la única forma de llegar a un plan es buscarlo.
 */
export function PlanesLista({
  planes,
  onSetPlan,
  onAddPlan,
  onRemovePlan,
  onSetBase,
  onToggleVenta,
}: {
  planes: PlanForm[];
  onSetPlan: (i: number, k: keyof PlanForm, v: string) => void;
  onAddPlan: () => void;
  onRemovePlan: (i: number) => void;
  onSetBase: (i: number) => void;
  /** Marca el plan inicial. `-1` lo desmarca (la organización vuelve a usar el base). */
  /** Prende/apaga la venta de un plan. */
  onToggleVenta: (i: number, valor: boolean) => void;
}) {
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState<number | null>(null);

  // Se filtra conservando el índice real: los callbacks (quitar, marcar base)
  // operan sobre la posición en el array original, no sobre la vista filtrada.
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    const conIndice = planes.map((p, i) => ({ p, i }));
    if (!t) return conIndice;
    return conIndice.filter(
      ({ p }) =>
        p.codigo.toLowerCase().includes(t) ||
        (p.nombre ?? '').toLowerCase().includes(t) ||
        (p.sucursalId ?? '').toLowerCase().includes(t),
    );
  }, [planes, q]);

  const base = planes.findIndex((p) => p.esBase);

  // Cuántas filas tiene cada código: el mismo plan puede estar contratado en
  // varias sucursales del empleador y cada una es una fila; se marca para que
  // se vea que no es un duplicado por error.
  const filasPorCodigo = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of planes) {
      const c = p.codigo.trim();
      if (c) m.set(c, (m.get(c) ?? 0) + 1);
    }
    return m;
  }, [planes]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <strong style={{ fontSize: 13 }}>
          {visibles.length === planes.length
            ? `${planes.length} ${planes.length === 1 ? 'plan' : 'planes'}`
            : `${visibles.length} de ${planes.length} planes`}
        </strong>
        {base >= 0 && (
          <span className="text-element-mid" style={{ fontSize: 12 }}>
            base: <code>{planes[base].codigo || '—'}</code>
          </span>
        )}
        <input
          className="input"
          style={{ maxWidth: 240, marginLeft: 'auto' }}
          placeholder="Buscar por código, nombre o sucursal…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className="btn btn-ghost" onClick={onAddPlan}>
          <PlusIcon size={14} /> Agregar
        </button>
      </div>

      {planes.length === 0 && (
        <p className="text-element-mid" style={{ fontSize: 13 }}>
          Sin planes. Traé la config desde Oracle o agregá uno a mano.
        </p>
      )}

      {planes.length > 0 && visibles.length === 0 && (
        <p className="text-element-mid" style={{ fontSize: 13 }}>
          Ningún plan coincide con «{q}».
        </p>
      )}

      <div
        style={{
          maxHeight: 460,
          overflowY: 'auto',
          border: visibles.length ? '1px solid var(--color-outline)' : 'none',
          borderRadius: 8,
        }}
      >
        {visibles.map(({ p, i }) => {
          const expandido = abierto === i;
          return (
            <div
              key={`${p.codigo}-${i}`}
              style={{
                borderBottom: '1px solid var(--color-outline)',
                background: p.esBase ? 'var(--color-surface-accent, #f3f6fb)' : undefined,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '7px 10px',
                }}
              >
                <button
                  type="button"
                  className="btn btn-link"
                  style={{ padding: 0, color: 'var(--color-element-mid)' }}
                  title={expandido ? 'Ocultar códigos' : 'Ver códigos de Binary'}
                  onClick={() => setAbierto(expandido ? null : i)}
                >
                  {expandido ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </button>

                <code style={{ fontSize: 12.5, minWidth: 130 }}>{p.codigo || '(sin código)'}</code>

                <span
                  className="text-element-mid"
                  style={{
                    fontSize: 12.5,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={p.nombre}
                >
                  {p.nombre || '—'}
                </span>

                <span
                  className="text-element-mid"
                  style={{ fontSize: 11.5 }}
                  title={
                    (filasPorCodigo.get(p.codigo.trim()) ?? 0) > 1
                      ? `${p.codigo} está contratado en ${filasPorCodigo.get(p.codigo.trim())} sucursales: cada fila es una opción distinta al cargar un padrón.`
                      : undefined
                  }
                >
                  suc {p.sucursalId || '—'}
                  {(filasPorCodigo.get(p.codigo.trim()) ?? 0) > 1 &&
                    ` · 1 de ${filasPorCodigo.get(p.codigo.trim())} sucursales`}
                </span>

                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}
                  title="El plan base es el que cubre la empresa"
                >
                  <input
                    type="radio"
                    name="planBase"
                    checked={!!p.esBase}
                    onChange={() => onSetBase(i)}
                  />
                  base
                </label>

                {/* Un plan sin el flag cuenta como disponible: la config vieja
                    no lo tenía y apagarlos a todos dejaría a la organización sin
                    nada para vender. */}
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}
                  title="Disponible para la venta: si lo apagás, un promotor no puede elegir este plan al cargar un padrón (los integrantes de un titular que ya está en él se dan de alta igual)"
                >
                  <input
                    type="checkbox"
                    checked={p.disponibleVenta !== false}
                    onChange={(e) => onToggleVenta(i, e.target.checked)}
                  />
                  en venta
                </label>

                <button
                  type="button"
                  className="btn btn-link"
                  style={{ color: 'var(--color-error-dark)', padding: 0 }}
                  title="Quitar plan"
                  onClick={() => onRemovePlan(i)}
                >
                  <TrashIcon size={14} />
                </button>
              </div>

              {expandido && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: 8,
                    padding: '4px 10px 12px 34px',
                  }}
                >
                  <CampoPlan
                    label="Código (PLAN_CODIGO)"
                    value={p.codigo}
                    onChange={(v) => onSetPlan(i, 'codigo', v)}
                    placeholder="ej: MS_12490"
                  />
                  <CampoPlan
                    label="Nombre"
                    value={p.nombre}
                    onChange={(v) => onSetPlan(i, 'nombre', v)}
                    placeholder="PLAN MS 1"
                  />
                  <CampoPlan
                    label="Sucursal (SUC_EMP)"
                    value={p.sucursalId}
                    onChange={(v) => onSetPlan(i, 'sucursalId', v)}
                    placeholder="74000"
                  />
                  <CampoPlan
                    label="Categoría (CATAFI)"
                    value={p.categoriaAfiliadoCodigo}
                    onChange={(v) => onSetPlan(i, 'categoriaAfiliadoCodigo', v)}
                    placeholder="DISK"
                  />
                  <CampoPlan
                    label="AA"
                    value={p.aaCodigo}
                    onChange={(v) => onSetPlan(i, 'aaCodigo', v)}
                    placeholder="AEMPCD"
                  />
                  <CampoPlan
                    label="AA2"
                    value={p.aa2Codigo}
                    onChange={(v) => onSetPlan(i, 'aa2Codigo', v)}
                    placeholder="SIN-DESC"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CampoPlan({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label style={{ display: 'block', fontSize: 11.5, color: 'var(--color-element-mid)' }}>
      {label}
      <input
        className="input"
        style={{ marginTop: 2 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}
